import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import '../../assets/css/views/productsManagement.css';
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";


import Loader from "../../components/Loader/loader";
import { Button, Card, IconFunnel, IconSearch, IconWarningAlt, Search, Select, SelectOption, Table, TableColumns, TableData, Toggle } from "@veneer/core";
import appointmentService from "../../services/agendaApi/appointment.service";
import { ISubretailDto } from "../../models/dto/agendaApi/ISubretailDto";
import productService from "../../services/generalApi/product.service";
import { IProductBySubretailDto } from "../../models/dto/generalApi/IProductBySubretailDto";
import { IProductStatusIn } from "../../models/input/generalApi/IProductStatusIn";
import { ICategoryDto } from "../../models/dto/trainingApi/IMaterialDto";

type SelectValue = number | string | boolean;


interface Pagination {
    currentPage: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
    products: IProductBySubretailDto[];
}

interface PaginationProps {
    currentPage: number,
    onPageChange: any,
    pageSize: number,
    totalItems: number,
}

interface FilterProps {
    sku: string,
    marketingName: string,
    category: SelectValue[],
}


export default function ProductsManagement() {
    const [t] = useTranslation("global");
    const languageId: any = useSelector((state: RootState) => state.languageId);
    type SelectValue = number | string | boolean;


    const [loading, setLoading] = useState<boolean>(false);

    const [tableData, setTableData] = useState<TableData[]>([]);
    const [tableDataFiltered, setTableDataFiltered] = useState<TableData[]>([]);

    const [subretails, setSubretails] = useState<SelectOption[]>([]);
    const [subretailSelected, setSubretailSelected] = useState<SelectValue[]>([]);
    const subretailSelectedRef = useRef<number>(NaN);

    const [categories, setCategories] = useState<SelectOption[]>([]);
    const [categorySelected, setCategorySelected] = useState<SelectValue[]>([]);

    const [disableSubretail, setDisableSubretail] = useState<boolean>(true);
    const [disableCategories, setDisableCategories] = useState<boolean>(true);


    const [search, setSearch] = useState<string>('');
    const [searchMarketingName, setSearchMarketingName] = useState<string>('');

    const [disableSearch, setDisableSearch] = useState<boolean>(true);
    const [disableSearchName, setDisableSearchName] = useState<boolean>(true);

    const [disableSearchCategory, setDisableSearchCategory] = useState<boolean>(true);

    const [searchError, setSearchError] = useState<boolean>(true);

    const appliedFilterRef = useRef<FilterProps>({ sku: '', marketingName: '', category: [] });
    const pageNumberRef = useRef<number>(1);

    const [showFilters, setShowFilters] = useState<boolean>(false);


    useEffect(() => {
        getSubretailData();
        getCategoriesData();

    }, [])


    useEffect(() => {
        setSearchError(false);
        if (search && search.length > 1 && tableData.length > 0) {
            setDisableSearch(false);
        } else {
            setDisableSearch(true);
        }
    }, [search])

    useEffect(() => {
        setSearchError(false);
        if (searchMarketingName && searchMarketingName.length > 1 && tableData.length > 0) {
            setDisableSearchName(false);
        } else {
            setDisableSearchName(true);
        }
    }, [searchMarketingName])


    useEffect(() => {
        setSearchError(false);
        if (categorySelected.length > 0 && tableData.length > 0) {
            setDisableSearchCategory(false);
        } else {
            setDisableSearchCategory(true);
        }
    }, [categorySelected])

    useEffect(() => {
        if (subretailSelected.length > 0) {
            getProductsData(Number(subretailSelected), pageNumberRef.current, appliedFilterRef.current)
        }
    }, [subretailSelected])


    const getSubretailData = async () => {
        try {
            const subrteailsList = await appointmentService.getSubretailList();
            const resultSubretails = fillDataSubretails(subrteailsList);
            setSubretails(resultSubretails);
            setDisableSubretail(false)
        } catch (error) {
            console.error(error)
            setDisableSubretail(true)
        }
    }

    const getCategoriesData = async () => {
        try {
            const categoriesList = await productService.getCategoriesList(languageId.value, "L2");
            const resultCategories = fillDataCategories(categoriesList);
            setCategories(resultCategories);
            setDisableCategories(false)
        } catch (error) {
            console.error(error)
            setDisableCategories(true)
        }
    }

    const fillDataSubretails = (data: ISubretailDto[]) => {
        const dataTemp: SelectOption[] = [];
        for (let i = 0; i < data.length; i++) {
            const subretail: ISubretailDto = data[i];
            let selectData: SelectOption;
            selectData = {
                value: subretail.fieldID,
                label: subretail.fieldValue,
            };
            dataTemp.push(selectData);
        }
        return dataTemp;
    };

    const fillDataCategories = (data: ICategoryDto[]) => {
        const dataTemp: SelectOption[] = [];
        for (let i = 0; i < data.length; i++) {
            const category: ICategoryDto = data[i];
            let selectData: SelectOption;
            selectData = {
                value: category.categoryID,
                label: category.category,
            };
            dataTemp.push(selectData);
        }
        return dataTemp;
    };


    const getProductsData = (subretailID: number, pageNumber: number, filter: FilterProps) => {
        setLoading(true);
        const category = filter?.category?.length > 0 ? Number(filter.category) : null;
        try {
            productService.getProductsBySubretail(pageNumber, 50, subretailID, 2, filter.sku, filter.marketingName, category)
                .then((r: Pagination) => {
                    setPaginationProps((prevProps) => ({
                        ...prevProps,
                        currentPage: r.currentPage,
                        pageSize: r.pageSize,
                        totalItems: r.totalRecords
                    }));

                    fillProductsData(r.products);
                })
        } catch (error) {
            console.error(error);
        }
    }

    const handlePageChange = (pageNumber: number) => {
        if (isNaN(pageNumber) || pageNumber < 1) {
            pageNumber = 1;
        }
        if(!isNaN(subretailSelectedRef.current)){
            pageNumberRef.current = pageNumber;
            getProductsData(subretailSelectedRef.current, pageNumberRef.current, appliedFilterRef.current);    
        }
  };

    const [paginationProps, setPaginationProps] = useState<PaginationProps>({ currentPage: 1, onPageChange: handlePageChange, pageSize: 0, totalItems: 0 });


    const fillProductsData = (data: IProductBySubretailDto[]) => {
        const pagedData: TableData[] = [];
        for (let i = 0; i < data.length; i++) {
            const product: IProductBySubretailDto = data[i];
            let tableData: TableData = {};
            tableData = {
                'marketingName': product.marketingName,
                'sku': product.sku,
                'subRetailID': product.subRetailID,
                'category': product.category,
                'active': <Toggle id={"toggle" + product.sku} defaultOn={product.active} onChange={() => handleChangeActive(product.sku, !product.active)} />,
            }
            pagedData.push(tableData);
        }
        setTableData(pagedData);
        setTableDataFiltered(pagedData);
        setLoading(false);
    }

    const handleSearch = () => {
        pageNumberRef.current = 1;
        if (search) {
            appliedFilterRef.current.sku = search;
            getProductsData(Number(subretailSelected), 1, appliedFilterRef.current);
        } else {
            setSearchError(true)
        }
    }

    const onClearSearch = () => {
        setSearch('');
        appliedFilterRef.current.sku = '';
        pageNumberRef.current = 1;
        if (subretailSelected.length) {
            getProductsData(Number(subretailSelected), 1, appliedFilterRef.current);
        }
    }

    const handleSearchName = () => {
        pageNumberRef.current = 1;
        if (searchMarketingName) {
            appliedFilterRef.current.marketingName = searchMarketingName;
            getProductsData(Number(subretailSelected), 1, appliedFilterRef.current);
        } else {
            setSearchError(true)
        }
    }

    const onClearSearchName = () => {
        setSearchMarketingName('');
        appliedFilterRef.current.marketingName = '';
        pageNumberRef.current = 1;
        if (subretailSelected.length > 0) {
            getProductsData(Number(subretailSelected), 1, appliedFilterRef.current);
        }
    }

    const handleSearchCategory = () => {
        if (categorySelected.length > 0) {
            appliedFilterRef.current.category = categorySelected;
            getProductsData(Number(subretailSelected), 1, appliedFilterRef.current);
        }
    }

    const onClearSearchCategory = () => {
        setCategorySelected([]);
        appliedFilterRef.current.category = [];
        getProductsData(Number(subretailSelected), 1, appliedFilterRef.current);

    }

    const columns: TableColumns[] = [
        {
            id: 'marketingName',
            label: t("header.marketingName"),
            filter: (
                <div className="productsManagementTableFilters">
                    <Search
                        label="Search"
                        id="table-filter-products-management"
                        value={searchMarketingName}
                        onChange={value => setSearchMarketingName(String(value))}
                        onClear={onClearSearchName}
                    />
                    <Button disabled={disableSearchName} className="buttonSearchProductsManagement" onClick={handleSearchName} leadingIcon={<IconSearch />} />

                </div>
            ),
        },
        {
            id: 'sku',
            label: t("header.sku"),
            filter: (
                <div className="productsManagementTableFilters">
                    <Search
                        label="Search"
                        id="table-filter-products-management"
                        value={search}
                        onChange={value => setSearch(String(value))}
                        onClear={onClearSearch}
                    />
                    <Button disabled={disableSearch} className="buttonSearchProductsManagement" onClick={handleSearch} leadingIcon={<IconSearch />} />

                </div>
            ),
        },
        {
            id: 'category',
            label: t("header.category"),
            filter: (
                <div className="trainingMaterialTableFilters">
                    <Select
                        className="trainingMaterialSelect"
                        label={t("header.category")}
                        onChange={e => setCategorySelected([e.value])}
                        onClear={onClearSearchCategory}
                        options={categories}
                        value={categorySelected}
                        placement="bottom-start"
                        disabled={disableCategories}
                    />
                    <Button disabled={disableSearchCategory} className="buttonSearchTrainingMaterial" onClick={handleSearchCategory} leadingIcon={<IconSearch />} />

                </div>
            ),
        },
        {
            id: 'active',
            label: t("header.active"),
        }

    ];



    const handleChangeSubretail = (option: any) => {
        setSubretailSelected([option])
        subretailSelectedRef.current = Number(option);
    }

    const onClearSubretail = () => {
        setSubretailSelected([])
        setTableData([])
        setTableDataFiltered([])
        setPaginationProps({ currentPage: 1, onPageChange: handlePageChange, pageSize: NaN, totalItems: NaN })
        setSearch('')
        setSearchMarketingName('')
        appliedFilterRef.current.sku = ''
        appliedFilterRef.current.marketingName = ''
        appliedFilterRef.current.category = []
        setShowFilters(false)
        subretailSelectedRef.current = NaN;

    }

    const handleChangeActive = (sku: string, active: boolean) => {
        setLoading(true)
        const updateProductStatusIn: IProductStatusIn = {
            status: Number(active),
            sku: sku,
            subretailID: Number(subretailSelected)
        }
        productService.updateProductStatus(updateProductStatusIn)
            .then(r => {
                getProductsData(Number(subretailSelected), pageNumberRef.current, appliedFilterRef.current);
            })
            .catch(error => {
                setLoading(false);
                console.error("Error updating training material status:", error);
            });
    }


    return (
        <div className="productsManagement">
            {loading && <Loader />}

            <h4 className="productsManagementTitle">{t("header.productsManagement")}</h4>
            <div className="productsManagementTop">
                <Select
                    className="productsManagementSelect"
                    label={t("header.subRetail")}
                    onChange={e => handleChangeSubretail(e.value)}
                    onClear={onClearSubretail}
                    options={subretails}
                    value={subretailSelected}
                    disabled={disableSubretail}
                    placement="bottom-start"
                />
                <Button appearance="secondary" onClick={() => setShowFilters(!showFilters)} leadingIcon={<IconFunnel />} />
            </div>

            <Card
                appearance="outlined"
                className="trainingMaterialCard"
                content={<>
                    <Table
                        className="trainingMaterialTable"
                        behavior={{
                            type: 'singlePage',
                        }}
                        pagination={paginationProps}
                        columnResize
                        showFilters={showFilters}
                        loading={loading}
                        columns={columns}
                        data={tableDataFiltered}
                        loadingDataLength={10}
                        customNoItems={<div className="customNoItems"><div className="DivIconCustomNoItems"><IconWarningAlt className="IconCustomNoItems" /></div>{t("header.noItemsFound")}</div>}
                    />
                </>
                }
            />



        </div>
    );
}